export function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Deep equality check for objects and arrays
 * Works in both Node.js and browser environments
 */
export function isEqual(a: unknown, b: unknown): boolean {
  // Same reference or primitive equality
  if (a === b) return true;

  // If either is null/undefined and they're not equal, return false
  if (isNil(a) || isNil(b)) return false;

  // Different types
  if (typeof a !== typeof b) return false;

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  // Handle Objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every(
      (key) =>
        Object.hasOwn(b, key) &&
        isEqual(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key]
        )
    );
  }

  // Primitives (string, number, boolean) - already handled by ===
  return false;
}

export function filterObj<T>(
  fn: (k: string, v: T) => boolean,
  obj: Record<string, T>
) {
  const res: Record<string, T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (fn(k, v)) {
      res[k] = v;
    }
  }
  return res;
}

export function mapObj<T, Y>(
  fn: (k: string, v: T) => [string, Y],
  obj: Record<string, T>
) {
  const res: Record<string, Y> = {};
  for (const [k, v] of Object.entries(obj)) {
    const [newK, newV] = fn(k, v);
    res[newK] = newV;
  }
  return res;
}

export function filterNilValues<T>(obj: Record<string, T>) {
  const result = filterObj((_k, v) => !isNil(v), obj);
  return result;
}

export function coalesceBlankPropsToNull<T extends Record<string, unknown>>(
  obj: T
): { [K in keyof T]: T[K] | null } {
  return mapObj((k, v) => [k, v || null], obj) as {
    [K in keyof T]: T[K] | null;
  };
}

export function placeKeyInData<T>(keyName: string, obj: Record<string, T>) {
  if (!keyName || typeof keyName !== 'string') {
    throw new Error('keyName must be provided to placeKeyInData(keyName, obj)');
  }
  return mapObj(
    (key, value) => [
      key,
      {
        ...value,
        [keyName]: key,
      },
    ],
    obj
  );
}

export function listOfKeys<T>(obj: Record<string, T>) {
  return Object.entries(obj)
    .filter(([, v]) => v)
    .map(([k]) => k);
}

export function listOfValues<T>(obj: Record<string, T>) {
  return Object.entries(obj)
    .filter(([, v]) => v)
    .map(([, v]) => v);
}

export * from './any-value-defined';
export * from './check-object-keys';
export * from './enum';
